// ABOUTME: Modal for creating workspaces with real-time provisioning progress.
// ABOUTME: Shows live updates as Service Principal and catalog are provisioned.

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Building2, Check, Loader2, AlertCircle, Terminal } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { workspaceApi } from '@/services/api/adminClient';
import type { WorkspaceCreateRequest, WorkspaceDetail, SubscriptionTier } from '@/types/admin';

interface CreateWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (workspace: WorkspaceDetail) => void;
}

const TIERS: SubscriptionTier[] = ['freemium', 'basic', 'standard', 'enterprise', 'enterprise_plus'];

type ProvisioningStep = 'workspace' | 'service_principal' | 'catalog' | 'complete';
type StepStatus = 'pending' | 'in_progress' | 'success' | 'error';

interface ProvisioningState {
  currentStep: ProvisioningStep;
  workspace: StepStatus;
  service_principal: StepStatus;
  catalog: StepStatus;
  error?: string;
  workspaceId?: string;
}

export function CreateWorkspaceModal({
  isOpen,
  onClose,
  onCreated,
}: CreateWorkspaceModalProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [tier, setTier] = useState<SubscriptionTier>('freemium');
  const [status, setStatus] = useState<'active' | 'trial'>('trial');
  const [error, setError] = useState<string | null>(null);

  // Provisioning state
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [provisioningState, setProvisioningState] = useState<ProvisioningState>({
    currentStep: 'workspace',
    workspace: 'pending',
    service_principal: 'pending',
    catalog: 'pending',
  });

  const pollIntervalRef = useRef<number | undefined>(undefined);
  const createdWorkspaceRef = useRef<WorkspaceDetail | null>(null);

  // Auto-generate slug from name
  useEffect(() => {
    const generatedSlug = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 50);
    setSlug(generatedSlug);
  }, [name]);

  // Poll workspace status during provisioning
  useEffect(() => {
    if (!isProvisioning || !provisioningState.workspaceId) return;

    const pollStatus = async () => {
      try {
        const workspace = await workspaceApi.get(provisioningState.workspaceId!);

        // Update provisioning state based on workspace status
        const newState: Partial<ProvisioningState> = {};

        // Service Principal status
        if (workspace.service_principal?.status === 'active') {
          newState.service_principal = 'success';
        } else if (workspace.service_principal?.status === 'failed') {
          newState.service_principal = 'error';
          newState.error = workspace.service_principal.error_message || 'Service Principal provisioning failed';
        } else if (workspace.service_principal?.status === 'provisioning') {
          newState.service_principal = 'in_progress';
          newState.currentStep = 'service_principal';
        }

        // Catalog status
        if (workspace.writeable_catalog) {
          newState.catalog = 'success';
          newState.currentStep = 'complete';

          // Stop polling - provisioning complete!
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }

          // Store workspace for onCreated callback
          createdWorkspaceRef.current = workspace;
        } else if (newState.service_principal === 'success') {
          newState.catalog = 'in_progress';
          newState.currentStep = 'catalog';
        }

        setProvisioningState(prev => ({ ...prev, ...newState }));

      } catch (err) {
        console.error('Failed to poll workspace status:', err);
      }
    };

    // Poll every 1 second
    pollIntervalRef.current = window.setInterval(pollStatus, 1000);

    // Initial poll
    pollStatus();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isProvisioning, provisioningState.workspaceId]);

  const handleCreate = async () => {
    if (!name.trim() || !slug.trim()) {
      setError('Name and slug are required');
      return;
    }

    setError(null);
    setIsProvisioning(true);

    // Reset provisioning state
    setProvisioningState({
      currentStep: 'workspace',
      workspace: 'in_progress',
      service_principal: 'pending',
      catalog: 'pending',
    });

    try {
      const request: WorkspaceCreateRequest = {
        name: name.trim(),
        slug: slug.trim(),
        subscription_tier: tier,
        subscription_status: status,
      };

      const workspace = await workspaceApi.create(request);

      // Workspace created! Update state and start polling
      setProvisioningState(prev => ({
        ...prev,
        workspace: 'success',
        service_principal: 'in_progress',
        currentStep: 'service_principal',
        workspaceId: workspace.id,
      }));

    } catch (err) {
      setProvisioningState(prev => ({
        ...prev,
        workspace: 'error',
        error: err instanceof Error ? err.message : 'Failed to create workspace',
      }));
      setError(err instanceof Error ? err.message : 'Failed to create workspace');
      setIsProvisioning(false);
    }
  };

  const handleClose = () => {
    // Only allow closing if not provisioning or if there's an error
    if (isProvisioning && provisioningState.currentStep !== 'complete' && !provisioningState.error) {
      return;
    }

    // Call onCreated if workspace was successfully created
    if (createdWorkspaceRef.current) {
      onCreated(createdWorkspaceRef.current);
      createdWorkspaceRef.current = null;
    }

    // Clean up polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Reset state
    setName('');
    setSlug('');
    setTier('freemium');
    setStatus('trial');
    setError(null);
    setIsProvisioning(false);
    setProvisioningState({
      currentStep: 'workspace',
      workspace: 'pending',
      service_principal: 'pending',
      catalog: 'pending',
    });

    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={isProvisioning ? undefined : handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-2xl bg-surface-0 border border-surface-200 shadow-modal rounded-sm overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-surface-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-primary-50 flex items-center justify-center">
                {isProvisioning ? (
                  <Terminal className="w-5 h-5 text-primary-600" />
                ) : (
                  <Building2 className="w-5 h-5 text-primary-600" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-surface-800">
                  {isProvisioning ? 'Provisioning Workspace' : 'Create Workspace'}
                </h2>
                {isProvisioning && (
                  <p className="text-xs text-surface-500 mt-0.5">
                    Real-time deployment status
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isProvisioning && provisioningState.currentStep !== 'complete' && !provisioningState.error}
              className="p-2 hover:bg-surface-100 rounded-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <X className="w-5 h-5 text-surface-500" />
            </button>
          </div>

          <div className="p-6">
            {!isProvisioning ? (
              // Form View
              <>
                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 p-3 rounded-sm bg-error-light border border-error/30 text-error text-sm"
                  >
                    {error}
                  </motion.div>
                )}

                {/* Form */}
                <div className="space-y-4 mb-6">
                  <Input
                    label="Workspace Name"
                    placeholder="My Workspace"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={!name.trim() && error ? 'Name is required' : undefined}
                  />

                  <Input
                    label="Slug"
                    placeholder="my-workspace"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    helperText="URL-friendly identifier (lowercase letters, numbers, and hyphens only)"
                    error={!slug.trim() && error ? 'Slug is required' : undefined}
                  />

                  <div>
                    <label className="block text-sm font-medium text-surface-600 mb-1.5">
                      Subscription Tier
                    </label>
                    <select
                      value={tier}
                      onChange={(e) => setTier(e.target.value as SubscriptionTier)}
                      className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50 transition-all"
                    >
                      {TIERS.map((t) => (
                        <option key={t} value={t}>
                          {t.replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-600 mb-1.5">
                      Subscription Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as 'active' | 'trial')}
                      className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50 transition-all"
                    >
                      <option value="trial">Trial</option>
                      <option value="active">Active</option>
                    </select>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button variant="ghost" onClick={handleClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreate}
                    disabled={!name.trim() || !slug.trim()}
                    className="flex-1"
                    leftIcon={<Building2 className="w-4 h-4" />}
                  >
                    Create Workspace
                  </Button>
                </div>
              </>
            ) : (
              // Provisioning Progress View
              <ProvisioningProgress
                state={provisioningState}
                workspaceName={name}
                onComplete={handleClose}
              />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Provisioning Progress Component
function ProvisioningProgress({
  state,
  workspaceName,
  onComplete,
}: {
  state: ProvisioningState;
  workspaceName: string;
  onComplete: () => void;
}) {
  const isComplete = state.currentStep === 'complete';
  const hasError = !!state.error;

  return (
    <div className="space-y-6">
      {/* Live Status Header */}
      <div className="flex items-center gap-3 p-4 rounded-sm bg-surface-50 border border-surface-200">
        <div className="w-2 h-2 rounded-full bg-success animate-pulse shadow-[0_0_8px_rgba(13,178,136,0.4)]" />
        <span className="text-sm font-mono text-surface-600">
          PROVISIONING: <span className="text-surface-800 font-semibold">{workspaceName}</span>
        </span>
      </div>

      {/* Provisioning Steps */}
      <div className="space-y-3">
        <ProvisioningStep
          title="Create Workspace"
          description="Initializing workspace configuration"
          status={state.workspace}
          isActive={state.currentStep === 'workspace'}
        />

        <ProvisioningStep
          title="Provision Service Principal"
          description="Creating Databricks service principal and storing credentials"
          status={state.service_principal}
          isActive={state.currentStep === 'service_principal'}
        />

        <ProvisioningStep
          title="Create Writeable Catalog"
          description="Setting up Unity Catalog with managed storage"
          status={state.catalog}
          isActive={state.currentStep === 'catalog'}
        />
      </div>

      {/* Error Display */}
      {hasError && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-sm bg-error-light border border-error/30"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-error mb-1">Provisioning Failed</div>
              <div className="text-xs text-error/80 font-mono">{state.error}</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Success Message */}
      {isComplete && !hasError && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-4 rounded-sm bg-success-light border border-success/30"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
              <Check className="w-5 h-5 text-success" />
            </div>
            <div>
              <div className="text-sm font-semibold text-success mb-1">
                Workspace Provisioned Successfully
              </div>
              <div className="text-xs text-success/80">
                All resources created and configured. Workspace is ready to use.
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Action Button */}
      <div className="flex justify-end">
        {isComplete || hasError ? (
          <Button onClick={onComplete} className="min-w-32">
            {isComplete ? 'Done' : 'Close'}
          </Button>
        ) : (
          <div className="text-xs text-surface-500 font-mono">
            Provisioning in progress...
          </div>
        )}
      </div>
    </div>
  );
}

// Individual Provisioning Step Component
function ProvisioningStep({
  title,
  description,
  status,
  isActive,
}: {
  title: string;
  description: string;
  status: StepStatus;
  isActive: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="relative"
    >
      {/* Step Container */}
      <div
        className={`
          relative p-4 rounded-sm border transition-all duration-300
          ${isActive ? 'bg-surface-0 border-primary-600/30 shadow-sm' : 'bg-surface-50 border-surface-200'}
          ${status === 'success' ? 'border-success/30' : ''}
          ${status === 'error' ? 'border-error/30' : ''}
        `}
      >
        <div className="flex items-start gap-4">
          {/* Status Icon */}
          <div className="flex-shrink-0 mt-0.5">
            {status === 'pending' && (
              <div className="w-6 h-6 rounded-full border-2 border-surface-200" />
            )}
            {status === 'in_progress' && (
              <div className="w-6 h-6 rounded-full border-2 border-warning/40 border-t-warning animate-spin" />
            )}
            {status === 'success' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-6 h-6 rounded-full bg-success/20 flex items-center justify-center"
              >
                <Check className="w-4 h-4 text-success" />
              </motion.div>
            )}
            {status === 'error' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-6 h-6 rounded-full bg-error/20 flex items-center justify-center"
              >
                <AlertCircle className="w-4 h-4 text-error" />
              </motion.div>
            )}
          </div>

          {/* Step Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3
                className={`
                  text-sm font-semibold transition-colors
                  ${status === 'success' ? 'text-success' : ''}
                  ${status === 'error' ? 'text-error' : ''}
                  ${status === 'in_progress' ? 'text-surface-800' : 'text-surface-600'}
                `}
              >
                {title}
              </h3>
              {status === 'in_progress' && (
                <Loader2 className="w-3 h-3 text-warning animate-spin" />
              )}
            </div>
            <p
              className={`
                text-xs mt-1 transition-colors font-mono
                ${status === 'in_progress' ? 'text-surface-500' : 'text-surface-500/60'}
              `}
            >
              {description}
            </p>

            {/* Progress Bar */}
            {status === 'in_progress' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-3 h-1 bg-surface-200 rounded-full overflow-hidden"
              >
                <motion.div
                  className="h-full bg-gradient-to-r from-warning to-warning/80"
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{
                    duration: 15,
                    ease: 'linear',
                    repeat: Infinity,
                  }}
                />
              </motion.div>
            )}
          </div>
        </div>

        {/* Active Step Glow Effect */}
        {isActive && status === 'in_progress' && (
          <motion.div
            className="absolute inset-0 rounded-sm bg-gradient-to-r from-primary-600/5 via-transparent to-transparent"
            animate={{
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        )}
      </div>
    </motion.div>
  );
}

export default CreateWorkspaceModal;
